import {
  type ADFDocument,
  type ADFDocumentContentNode,
  type ADFHardBreakNode,
  type ADFInlineNode,
  type ADFListItemNode
} from '@canvas/adf-types';

// Firefox supports Intl.Segmenter from version 125, but compat reports it as unsupported.
// eslint-disable-next-line compat/compat
const graphemeSegmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

type RevealCursor = {
  remainingGraphemes: number;
};

const getGraphemes = (text: string): string[] =>
  Array.from(graphemeSegmenter.segment(text), ({ segment }) => segment);

const revealInlineNode = (
  node: ADFInlineNode | ADFHardBreakNode,
  cursor: RevealCursor
): ADFInlineNode | ADFHardBreakNode | null => {
  if (cursor.remainingGraphemes === 0) {
    return null;
  }

  if (node.type !== 'text') {
    cursor.remainingGraphemes -= 1;
    return node;
  }

  const graphemes = getGraphemes(node.text);
  const visibleGraphemes = graphemes.slice(0, cursor.remainingGraphemes);
  cursor.remainingGraphemes -= visibleGraphemes.length;

  return visibleGraphemes.length > 0
    ? { ...node, text: visibleGraphemes.join('') }
    : null;
};

const revealListItem = (
  node: ADFListItemNode,
  cursor: RevealCursor
): ADFListItemNode | null => {
  const content = node.content
    ?.map((child) => revealDocumentContentNode(child, cursor))
    .filter(
      (child) =>
        child?.type === 'paragraph' ||
        child?.type === 'bulletList' ||
        child?.type === 'orderedList'
    );

  return content && content.length > 0 ? { ...node, content } : null;
};

const revealDocumentContentNode = (
  node: ADFDocumentContentNode,
  cursor: RevealCursor
): ADFDocumentContentNode | null => {
  switch (node.type) {
    case 'paragraph': {
      const content = node.content
        ?.map((child) => revealInlineNode(child, cursor))
        .filter((child) => child !== null);
      return content && content.length > 0 ? { ...node, content } : null;
    }
    case 'heading': {
      const content = node.content
        .map((child) => revealInlineNode(child, cursor))
        .filter(
          (child) =>
            child?.type === 'text' ||
            child?.type === 'emoji' ||
            child?.type === 'mention' ||
            child?.type === 'hardBreak'
        );
      return content.length > 0 ? { ...node, content } : null;
    }
    case 'bulletList': {
      const content = node.content
        ?.map((child) => revealListItem(child, cursor))
        .filter((child) => child !== null);
      return content && content.length > 0 ? { ...node, content } : null;
    }
    case 'orderedList': {
      const content = node.content
        ?.map((child) => revealListItem(child, cursor))
        .filter((child) => child !== null);
      return content && content.length > 0 ? { ...node, content } : null;
    }
    case 'listItem':
      return revealListItem(node, cursor);
    default: {
      const unsupportedNode: never = node;
      throw new Error(`Unsupported AI Summary preview node: ${String(unsupportedNode)}`);
    }
  }
};

const countInlineNodeGraphemes = (node: ADFInlineNode | ADFHardBreakNode): number =>
  node.type === 'text' ? getGraphemes(node.text).length : 1;

const countDocumentContentNodeGraphemes = (node: ADFDocumentContentNode): number => {
  switch (node.type) {
    case 'paragraph':
    case 'heading':
      return (
        node.content?.reduce(
          (count, child) => count + countInlineNodeGraphemes(child),
          0
        ) ?? 0
      );
    case 'bulletList':
    case 'orderedList':
    case 'listItem':
      return (
        node.content?.reduce(
          (count, child) => count + countDocumentContentNodeGraphemes(child),
          0
        ) ?? 0
      );
    default: {
      const unsupportedNode: never = node;
      throw new Error(`Unsupported AI Summary preview node: ${String(unsupportedNode)}`);
    }
  }
};

export const countAiSummaryPreviewGraphemes = (document: ADFDocument): number =>
  document.content.reduce(
    (count, node) => count + countDocumentContentNodeGraphemes(node),
    0
  );

export const createProgressiveAiSummaryPreview = (
  document: ADFDocument,
  visibleGraphemeCount: number
): ADFDocument => {
  const cursor = { remainingGraphemes: Math.max(0, visibleGraphemeCount) };
  const content = document.content
    .map((node) => revealDocumentContentNode(node, cursor))
    .filter((node) => node !== null);

  return content.length > 0
    ? { ...document, content: [content[0], ...content.slice(1)] }
    : {
        ...document,
        content: [{ type: 'paragraph', content: [] }]
      };
};
